import { TrashSimple } from '@phosphor-icons/react';
import { cloneElement, ReactElement, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AddressAttachedToType } from 'addresses/enums';
import { IRemoveAddressPayload } from 'addresses/types';
import { useContactGroupActions } from 'contacts/hooks';
import { IContactGroupAddress } from 'contacts/types';
import { Button, Confirmation, IButtonProps } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { Color, Variant } from 'theme/enums';

interface IProps extends IButtonProps {
  locations: IContactGroupAddress[];
  onSubmitSuccess?: (address?: IContactGroupAddress) => void;
  buttonElement?: ReactElement;
  notificationPosition?: NotificationPosition;
}

export const RemoveLocationButton = ({
  locations,
  onSubmitSuccess,
  buttonElement,
  notificationPosition = NotificationPosition.FORM,
  ...rest
}: IProps) => {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const { t } = useTranslation();

  const { removeContactGroupAddress } = useContactGroupActions(notificationPosition);

  const location = locations?.[0];

  const isDisabled = useMemo(() => {
    if (locations.length !== 1) {
      return true;
    }

    return locations.some(
      (location) => !!location.accountsReceivableId || !!location.accountsPayableId
    );
  }, [locations]);

  const getTitle = () => {
    return t('titleRemoveLocation', { locationName: location?.name });
  };

  const getMessage = () => {
    return t('messageConfirmRemoveLocation', { locationName: location?.name });
  };

  const onRemove = async () => {
    if (!location) {
      return null;
    }

    setIsConfirmationOpen(false);

    const payload: IRemoveAddressPayload = {
      address: { id: location.addressId },
      contactGroup: location.contactGroup?.id,
      attachedTo: AddressAttachedToType.CONTACT_GROUP,
    };

    const { success } = await removeContactGroupAddress(payload);

    if (!success) {
      return null;
    }

    setTimeout(() => {
      onSubmitSuccess?.(location);
    }, 1000);
  };

  const button = useMemo(() => {
    if (buttonElement) {
      return cloneElement(buttonElement, {
        onClick: () => setIsConfirmationOpen(true),
        disabled: isDisabled,
      });
    }

    return (
      <Button
        text={rest?.text || t('buttonLabelRemoveLocation')}
        variant={isDisabled ? Variant.OUTLINED : Variant.SOFT}
        color={Color.DANGER}
        startDecorator={<TrashSimple />}
        onClick={() => setIsConfirmationOpen(true)}
        disabled={isDisabled}
        {...rest}
      />
    );
  }, [buttonElement, rest, t, isDisabled]);

  return (
    <>
      {button}

      <Confirmation
        isOpen={isConfirmationOpen}
        title={getTitle()}
        message={getMessage()}
        confirmBtnLabel={t('buttonLabelRemove')}
        confirmBtnColor={Color.DANGER}
        onConfirm={onRemove}
        onCancel={() => setIsConfirmationOpen(false)}
      />
    </>
  );
};
